import { useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  InputAdornment,
  Button
} from '@mui/material';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import IconButton from 'components/@extended/IconButton';
import AnimateButton from 'components/@extended/AnimateButton';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import EyeInvisibleOutlined from '@ant-design/icons/EyeInvisibleOutlined';

// api
import { logInApi } from '../../api/loginApi';

export default function ChangePassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || '';

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event) => event.preventDefault();

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        email,
        password: '',
        NewPassword: values.newPassword
      };

      const res = await logInApi(payload);

      if (res.success) {
        toast.success('Password changed successfully. Please log in again.');
        navigate('/login');
      } else {
        toast.error(res.error || 'Password change failed');
      }
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
        px: 2
      }}
    >
      <Paper
        elevation={12}
        sx={{
          maxWidth: 500,
          width: '100%',
          borderRadius: 4,
          p: 5,
          boxShadow: '0px 12px 40px rgba(0,0,0,0.3)'
        }}
      >
        {/* Heading */}
        <Typography variant="h4" fontWeight="bold" mb={2} textAlign="center" color="primary">
          Change Password
        </Typography>

        {/* Show Email */}
        <Typography variant="subtitle1" textAlign="center" gutterBottom>
          You are changing the password for:
        </Typography>
        <Typography
          variant="body1"
          textAlign="center"
          sx={{
            fontWeight: 600,
            mb: 3,
            bgcolor: '#f5f5f5',
            p: 1.5,
            borderRadius: 2
          }}
        >
          {email}
        </Typography>

        {/* Form */}
        <Formik
          initialValues={{ newPassword: '' }}
          validationSchema={Yup.object().shape({
            newPassword: Yup.string()
              .min(6, 'Password must be at least 6 characters')
              .required('New password is required')
          })}
          onSubmit={handleSubmit}
        >
          {({ errors, handleBlur, handleChange, touched, values, handleSubmit, isSubmitting }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <div>
                  <InputLabel htmlFor="new-password" sx={{ fontWeight: 600 }}>
                    New Password
                  </InputLabel>
                  <OutlinedInput
                    fullWidth
                    error={Boolean(touched.newPassword && errors.newPassword)}
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={values.newPassword}
                    name="newPassword"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="Enter new password"
                    sx={{ borderRadius: 3 }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          color="secondary"
                        >
                          {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {touched.newPassword && errors.newPassword && (
                    <FormHelperText error>{errors.newPassword}</FormHelperText>
                  )}
                </div>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ mt: -1 }}
                >
                  ⚠️ Remember this password — you’ll need it next time you log in.
                </Typography>

                <AnimateButton>
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isSubmitting}
                    sx={{
                      fontWeight: 'bold',
                      textTransform: 'none',
                      borderRadius: 3,
                      py: 1.6,
                      fontSize: '1rem',
                      boxShadow: '0px 6px 18px rgba(0,0,0,0.2)'
                    }}
                  >
                    {isSubmitting ? 'Updating...' : 'Update Password'}
                  </Button>
                </AnimateButton>
              </Stack>
            </form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
}
